import React, {useEffect, useState} from 'react'
import {Card, Col, Divider, Image, Input, List, Pagination, Row, Space, Tag} from 'antd'
import {useRouteLoaderData, useSearchParams} from 'react-router-dom'
import {News} from '../../entities/news'
import './news.less'
import Markdown from 'react-markdown'
import {hastToReact} from 'property-information'
import urlProjectPhoto0 from 'url:../../../public/assets/images/house_garden_ar1.webp'
import urlProjectPhoto3 from 'url:../../../public/assets/images/IMG_1682.jpg'
import {CalendarOutlined} from '@ant-design/icons'
import dayjs from 'dayjs'
import {useIntl} from 'react-intl'
import {instance as newsService} from '../../services/NewsService'
hastToReact['src']

interface NewsListPageParams {
  currentPage: number
  pageSize: number
  total: number
  searchQuery?: string
  tags?: []
}

export function NewsListPage() {
  const {newsList, pageParams} = useRouteLoaderData('newsList') as {
    newsList: News[]
    pageParams: NewsListPageParams
  }

  const [activeTags, setActiveTags] = useState({news: false, tutorials: false})

  const [searchParams, setSearchParams] = useSearchParams()

  const intl = useIntl()

  const handlePaginationChange = (current: number) =>
    setSearchParams((searchParams) => {
      const page = current

      const params = Object.fromEntries(searchParams)
      if (page !== 1) {
        params.page = String(page)
      } else {
        delete params.page
      }

      return params
    })

  const toggleTag = (value: 'news' | 'tutorials') => {
    setActiveTags({...activeTags, ...{[value]: !activeTags[value]}})
  }

  useEffect(() => {
    setSearchParams((searchParams) => {
      const params = Object.fromEntries(searchParams)
      if (activeTags.news || activeTags.tutorials) {
        params.tags = (Object.entries(activeTags) as [keyof typeof activeTags, boolean][])
          .filter(([, v]) => v)
          .map(([key]) => key)
          .join(',')
      } else {
        delete params.tags
      }

      return params
    })
  }, [activeTags])

  return (
    <div className="news-list-page home-layout__content">
      <div className="news-list-page__header">
        <img className="news-list-page__header__image" src={urlProjectPhoto3} alt="News banner" />
      </div>

      <h1 className="news-list-page__title" style={{}}>
        {intl.formatMessage({id: `news`})}
      </h1>

      <div className="news-list-page__title__description">
        {intl.formatMessage({id: `theLatestPostsAndGuides`})}
      </div>

      <Row gutter={[16, 16]} align="middle" style={{marginBottom: 18}}>
        <Col xs={24} md={12} lg={10}>
          <Input.Search
            placeholder={intl.formatMessage({id: `searchForNews`})}
            allowClear
            size="large"
            onSearch={(value) =>
              setSearchParams((searchParams) => {
                const search = value

                const params = Object.fromEntries(searchParams)
                if (search !== '') {
                  params.search = String(search)
                } else {
                  delete params.search
                }

                return params
              })
            }
          />
        </Col>

        <Col xs={24} md={12} lg={14}>
          <Space wrap size={[10, 10]} style={{justifyContent: 'flex-end', width: '100%'}}>
            <Tag
              className="news-list-page__search__tag"
              style={{backgroundColor: activeTags.news ? 'rgba(244, 106, 42, 0.12)' : '#fff'}}
              onClick={() => toggleTag('news')}
            >
              {intl.formatMessage({id: `tag-news`})}
            </Tag>
            <Tag
              className="news-list-page__search__tag"
              style={{backgroundColor: activeTags.tutorials ? 'rgba(244, 106, 42, 0.12)' : '#fff'}}
              onClick={() => toggleTag('tutorials')}
            >
              {intl.formatMessage({id: `tag-tutorials`})}
            </Tag>
          </Space>
        </Col>
      </Row>

      <Divider style={{margin: '10px 0 18px'}} />

      <List
        dataSource={newsList}
        split={false}
        renderItem={(item) => (
          <Card className="news-list-page__list__news" key={item.id}>
            <Row gutter={16} wrap={false}>
              <Col flex="120px">
                <Image
                  className="news-list-page__list__news__image"
                  src={item.titlePhotoURL}
                  alt={item.newsName}
                  width={120}
                  height={90}
                  preview={false}
                />
              </Col>

              <Col flex="auto">
                <div className="news-list-page__list__news__info">
                  <a className="news-list-page__list__news__name" href={`/home/news/${item.url}`}>
                    {intl.locale === 'en' ? item.newsName : item.newsNameRU}
                  </a>

                  <div>
                    <span className="news-list-page__list__news__calendar">
                      <CalendarOutlined />
                      <span className="news-list-page__list__news__calendar__text">
                        {dayjs(item.dateOfCreating).format('ll')}
                      </span>
                    </span>
                    <Tag className="news-list-page__list__news__tag">
                      {intl.formatMessage({id: `tag-${item.newsType}`})}
                    </Tag>
                  </div>
                  <div className="news-list-page__list__news__text">{`${
                    intl.locale === 'en' ? item.text : item.textRU
                  }...`}</div>
                </div>
              </Col>
            </Row>
          </Card>
        )}
      />

      <div className="news-list-page__pagination">
        <Pagination
          defaultCurrent={1}
          total={pageParams.total}
          pageSize={newsService.DEFAULT_PER_PAGE}
          showSizeChanger={false}
          onChange={handlePaginationChange}
        />
      </div>
    </div>
  )
}
